import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';

import { applyVerticalStyles, interpolateString, sanitizeInnerHtml } from 'ctm-util-functions';

import styles from './label.css';

/**
 * A form label component, including main labels and sub-labels
 * @param {Object} props React props
 * @returns {Node} label html
 */
const Label = (props) => {
    const labelStyles = applyVerticalStyles(styles, 'label');
    const { schema, labelKey, identifier, children, variant, selectedValues } = props;
    const interpolatedContent = interpolateString(schema[labelKey])(selectedValues);

    /**
     * Gets the help indicator
     * @returns {Node} the help indicator
     */
    const getHelpIndicator = () => {
        if (!props.helpIndicator) return null;
        // prevents weird styling issue with sub label not being aligned with label
        if (props.labelKey === 'subLabel') return <div className={styles.no_helper_offset_spacing} />;
        return props.helpIndicator;
    };

    /**
     * Convert string to HTML node.
     * @param {String} content The tooltip content
     * @returns {Node} - The span with the content
     */
    const getHtml = (content) => {
        return <span dangerouslySetInnerHTML={{ __html: sanitizeInnerHtml(content) }} />;
    };

    const label = schema.labelFormat === 'html' ? getHtml(interpolatedContent) : interpolatedContent;
    const style = classNames(
        labelStyles[`variant_${schema.variant || variant}`],
        labelKey === 'subLabel' ? labelStyles.variant_sub_label : '',
        schema.requiredAsterisk ? labelStyles.requiredAsterisk : ''
    );

    return (
        <label className={style} htmlFor={schema.identifier || identifier}>
            {getHelpIndicator()}
            {label || children}
        </label>
    );
};

Label.propTypes = {
    schema: PropTypes.object,
    helpIndicator: PropTypes.object,
    selectedValues: PropTypes.object,
    labelKey: PropTypes.string,
    identifier: PropTypes.string,
    children: PropTypes.node,
    variant: PropTypes.string
};

Label.defaultProps = {
    labelKey: 'label',
    variant: 'default',
    schema: {},
    selectedValues: {}
};

export { Label };
